<script setup lang="ts">
import { MessageThreadChannelEnum } from "@/api/javaapi";
import { debounce } from "@/helpers/debounce.ts";
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from "vue";
import { type Ref, type ComponentPublicInstance } from "vue";
import { ProgressSpinner, InputText, IconField, InputIcon, Button, Popover } from "primevue";
import MessageThreadListItem from "@/components/messaging/MessageThreadListItem.vue";
import NewMessageForm from "@/components/messaging/NewMessageForm.vue";
import { useRouter, useRoute } from "vue-router";
import { useThreadStore } from "@/stores/thread.store";

const props = defineProps<{
  showNewMessage?: boolean;
  initialReservationNumber?: string;
}>();

const router = useRouter();
const route = useRoute();
const threadStore = useThreadStore();
const curOffset = ref(0);
const totalThreadCount = ref(0);
const searchValue = ref("");
const isLoading = ref(false);
const outOfData = ref(false);
const scrollArea: Ref<HTMLElement | null> = ref(null);
const content: Ref<HTMLElement | null> = ref(null);
const newMessagePopover = ref();
const newButton: Ref<ComponentPublicInstance | null> = ref(null);

const selectedThreadId = computed(() => Number(route.params.id) || null);

const threadList = computed(() => {
  const filteredThreads = searchValue.value
    ? threadStore.threadList
    : threadStore.threadList.filter(thread => !thread.archived);

  return filteredThreads.map((thread) => ({
    id: thread.ID ?? 0,
    name: thread.guestName ?? thread.subject ?? "Unknown",
    preview: thread.messages && thread.messages.length > 0 
      ? (thread.messages[thread.messages.length - 1].content ?? "") 
      : "",
    channel: (thread.channel ?? "UNKNOWN") as MessageThreadChannelEnum,
    new: thread.newStatus ?? false,
    date: thread.timestamp
      ? new Date(thread.timestamp).toLocaleDateString()
      : "N/A",
  }));
});

async function getThreadList(offset: number) {
  if (offset < 0) offset = 0;
  if (offset === 0) {
    outOfData.value = false;
    curOffset.value = 0;
    threadStore.threadList = [];
  }
  if (isLoading.value || outOfData.value) return;

  isLoading.value = true;
  try {
    const total = await threadStore.fetchThreadList(offset, size, searchValue.value);
    totalThreadCount.value = total;
    curOffset.value = threadStore.threadList.length;
    outOfData.value = threadStore.threadList.length >= totalThreadCount.value;
  } catch (error) {
    console.error("Failed to load threads:", error);
  } finally {
    isLoading.value = false;
  }
}

const loadMore = () => {
  if (!scrollArea.value || !content.value) return;
  const scrollBottom = scrollArea.value.scrollTop + scrollArea.value.offsetHeight;
  const contentHeight = content.value.offsetHeight;
  if (scrollBottom + 130 >= contentHeight && !isLoading.value && !outOfData.value) {
    getThreadList(curOffset.value);
  }
};

onMounted(() => {
  getThreadList(0);
  const debouncedThreadList = debounce((offset: number) => {
    getThreadList(offset);
  }, 500);
  watch(searchValue, () => debouncedThreadList(0));
  if (scrollArea.value) {
    scrollArea.value.addEventListener("scroll", loadMore);
  }
  // Automatically show popover if showNewMessage is true
  if (
    props.showNewMessage &&
    newMessagePopover.value
  ) {
    nextTick(() => {
      if (newButton.value) {
        const buttonElement = newButton.value.$el as HTMLElement;
        newMessagePopover.value.show({
          target: buttonElement,
          type: "click",
          clientX: buttonElement.getBoundingClientRect().left,
          clientY: buttonElement.getBoundingClientRect().top,
          currentTarget: buttonElement,
          bubbles: true,
          cancelable: true
        });
      }
    });
  }
});

function switchThread(id: number) {
  router.push(`/inbox/${id}`);
}

onUnmounted(() => {
  if (scrollArea.value) {
    scrollArea.value.removeEventListener("scroll", loadMore);
  }
});

const showNewMessagePopover = (event: Event) => {
  newMessagePopover.value.toggle(event);
};

const handleThreadCreated = (newThreadId: number) => {
  newMessagePopover.value.hide();
  router.push(`/inbox/${newThreadId}`);
};

const size = 20;
</script>

<template>
  <div class="h-full flex flex-col">
    <div class="p-4 flex-shrink-0 flex gap-2 items-center">
      <Button
        ref="newButton"
        label="New"
        icon="pi pi-plus"
        size="small"
        @click="showNewMessagePopover"
      />
      <IconField class="flex-1">
        <InputText
          v-model="searchValue"
          size="small"
          class="w-full"
          placeholder="Search threads..."
        />
        <InputIcon
          v-if="searchValue.length > 0"
          class="pi pi-times cursor-pointer text-gray-700 dark:text-gray-300"
          @click="searchValue = ''"
        />
      </IconField>
    </div>
    <div ref="scrollArea" class="flex-1 overflow-y-auto">
      <div ref="content">
        <MessageThreadListItem
          v-for="item in threadList"
          :key="item.id"
          :name="item.name"
          :preview="item.preview"
          :date="item.date"
          :channel="item.channel"
          :new="item.new"
          :class="{ 'bg-gray-300 dark:bg-gray-600': selectedThreadId === item.id }"
          @click="switchThread(item.id)"
        />
        <div class="flex justify-center py-4">
          <ProgressSpinner v-if="isLoading" class="w-8 h-8" />
        </div>
      </div>
    </div>

    <Popover ref="newMessagePopover">
      <NewMessageForm 
        @thread-created="handleThreadCreated"
        :initial-reservation-number="initialReservationNumber"
      />
    </Popover>
  </div>
</template>